import { ConfigProvider } from 'antd'
import arEG from 'antd/locale/ar_EG'
import enUS from 'antd/locale/en_US'
import { lazy, Suspense, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router-dom'
import { getThemeMode } from '../../store/Theme/ThemeSlice'
import { loadLanguage, setLanguage } from '../../store/Language/LanguageSlice'
import { getAntdTheme } from '../../theme/palette'
import { applyStoredUserLocale, syncUserLocalePreference } from '../../utilities/syncUserLocale'
import Loading from '../Loading/Loading';
import Assets from '../../pages/Assets/Assets'
import Activities from '../../pages/Activities/Activities'

const AppRoutes = () => {
  const dispatch = useDispatch()
  const { theme } = useSelector((state) => state.theme);
  const { language } = useSelector((state) => state.language);

  const cairoFont = "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Ubuntu', sans-serif";
  const antdTheme = getAntdTheme(theme, {
    fontFamily: language === 'ar' ? cairoFont : undefined,
  });

  useEffect(() => {
    dispatch(getThemeMode())
    dispatch(loadLanguage())
    applyStoredUserLocale(dispatch, setLanguage)
    syncUserLocalePreference()
  }, [dispatch])

  const PrivateRoute = lazy(() => import("../Routes/PrivateRoute"))
  const Orders = lazy(() => import("../../pages/Orders/Orders"))
  const OrdersCalendar = lazy(() => import("../../pages/Orders/OrdersCalendar"))
  const CreateInvoiceFromOrder = lazy(() => import("../../pages/Orders/CreateInvoiceFromOrder"))
  const AttachToInvoice = lazy(() => import("../../pages/Orders/AttachToInvoice"))
  const FormulaTypes = lazy(() => import("../../pages/FormulaTypes/FormulaTypes"))
  const Color = lazy(() => import("../../pages/Colors/Colors"))

  const Clients = lazy(() => import("../../pages/Clients/Clients"))
  const ClientDetails = lazy(() => import("../../pages/Clients/ClientDetails"))
  const WalletDetails = lazy(() => import("../../pages/Clients/WalletDetails"))
  const DoctorDetails = lazy(() => import("../../pages/Doctors/DoctorDetails"))
  const DoctorEdit = lazy(() => import("../../pages/Doctors/DoctorEdit"))
  const DoctorVersions = lazy(() => import("../../pages/Doctors/DoctorVersions"))
  const Transactions = lazy(() => import("../../pages/Transactions/Transactions"))
  const CreateOrder = lazy(() => import("../../pages/Orders/CreateOrder"))
  const Order = lazy(() => import("../../pages/Orders/Order"))
  const OrderVersions = lazy(() => import("../../pages/Orders/OrderVersions"))
  const OrderUpdate = lazy(() => import("../../pages/Orders/UpdateOrder"))
  const Invoice = lazy(() => import("../../pages/Invoices/Invoice"))
  const InvoiceVersions = lazy(() => import("../../pages/Invoices/InvoiceVersions"))
  const Detach = lazy(() => import("../../pages/Invoices/Detach"))
  const Invoices = lazy(() => import("../../pages/Invoices/Invoices"))
  const CreateInvoice = lazy(() => import("../../pages/Invoices/CreateInvoice"))
  const SummaryInvoice = lazy(() => import("../../pages/Invoices/SummaryInvoice"))
  const ExpenseTypes = lazy(() => import("../../pages/Expenses/ExpenseTypes"))
  const Expenses = lazy(() => import("../../pages/Expenses/Expenses"))
  const Home = lazy(() => import("../../pages/Home/Home"))
  const Users = lazy(() => import("../../pages/Users/Users"))
  const UserDetails = lazy(() => import("../../pages/Users/UserDetails"))
  const UserEdit = lazy(() => import("../../pages/Users/UserEdit"))
  const UserVersions = lazy(() => import("../../pages/Users/UserVersions"))
  const Profile = lazy(() => import("../../pages/Profile/Profile"))
  const NotFound = lazy(() => import("../../pages/404/404"))
  const PublicOrder = lazy(() => import("../../pages/PublicOrder"))
  const PublicInvoice = lazy(() => import("../../pages/PublicInvoice"))
  const ClientTransactions = lazy(() => import("../../pages/ClientTransactions/Index"))
  const Settings = lazy(() => import("../../pages/Settings/Index"))
  const LabInfo = lazy(() => import("../../pages/LabProfile/LabInfo"))
  const PaymentSettings = lazy(() => import("../../pages/LabProfile/PaymentSettings"))
  const DeliverySettings = lazy(() => import("../../pages/LabProfile/DeliverySettings"))
  const VacationsSettings = lazy(() => import("../../pages/LabProfile/VacationsSettings"))
  const BranchesSettings = lazy(() => import("../../pages/LabProfile/BranchesSettings"))
  const PlatformSettings = lazy(() => import("../../pages/PlatformSettings/Index"))
  // Backward-compatible aliases (legacy route variable names)
  const DoctorSettings = Settings
  const DoctorTransactions = ClientTransactions

  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path='/' element={
            <ConfigProvider locale={language === 'ar' ? arEG : enUS} direction={language === "ar" ? 'rtl' : 'ltr'} theme={antdTheme}>
              <PrivateRoute />
            </ConfigProvider>
          }>
            <Route exact path='/' element={<Home />} />
            <Route path='dashboard/*' element={<Home />} />
            <Route path='orders' element={<Orders />} />
            <Route path='orders/calendar' element={<OrdersCalendar />} />
            <Route path='orders/:id/create-invoice' element={<CreateInvoiceFromOrder />} />
            <Route path='orders/:id/attach-invoice' element={<AttachToInvoice />} />
            <Route path='transactions' element={<Navigate to="/setting/transactions" replace />} />
            <Route path='setting/activities' element={<Activities />} />
            <Route path='setting/transactions' element={<Transactions />} />
            <Route path='setting/expenses' element={<Expenses />} />
            <Route path='setting/expense-types' element={<ExpenseTypes />} />
            <Route path='platform-settings' element={<PlatformSettings />} />
            <Route path='platform-settings/colors' element={<Color />} />
            <Route path='platform-settings/formula-types' element={<FormulaTypes />} />
            <Route path='formula-types' element={<Navigate to="/platform-settings/formula-types" replace />} />
            <Route path='colors' element={<Navigate to="/platform-settings/colors" replace />} />
            <Route path='delivery-centers' element={<Navigate to="/clients" replace />} />
            <Route path='clients' element={<Clients />} />
            <Route path='clients/:id' element={<ClientDetails />} />
            <Route path='clients/list' element={<Navigate to="/clients" replace />} />
            <Route path='clients/delivery-centers' element={<Navigate to="/clients" replace />} />
            <Route path='clients/wallets' element={<Navigate to="/clients" replace />} />
            <Route path='clients/doctors' element={<Navigate to="/clients" replace />} />
            <Route path='wallets' element={<Navigate to="/clients" replace />} />
            <Route path='wallets/:id' element={<WalletDetails />} />
            <Route path='doctors' element={<Navigate to="/clients" replace />} />
            <Route path='doctors/:id' element={<DoctorDetails />} />
            <Route path='doctors/:id/edit' element={<DoctorEdit />} />
            <Route path='doctors/:id/versions' element={<DoctorVersions />} />
            <Route path='orders/create' element={<CreateOrder />} />
            <Route path='orders/:id/edit' element={<OrderUpdate />} />
            <Route path='orders/:id/versions' element={<OrderVersions />} />
            <Route path='orders/:id' element={<Order />} />
            <Route path='invoices' element={<Invoices />} />
            <Route path='invoices/create' element={<CreateInvoice />} />
            <Route path='invoices/summary' element={<SummaryInvoice />} />
            <Route path='invoices/:id' element={<Invoice />} />
            <Route path='invoices/:id/versions' element={<InvoiceVersions />} />
            <Route path='Invoice/:id' element={<Invoice />} />
            <Route path='invoices/:id/detach' element={<Detach />} />
            <Route path='ExpenseTypes' element={<Navigate to="/setting/expense-types" replace />} />
            <Route path='expenses' element={<Navigate to="/setting/expenses" replace />} />
            <Route path='setting' element={<Settings />} />
            <Route path='setting/lab-info' element={<LabInfo />} />
            <Route path='setting/payment' element={<PaymentSettings />} />
            <Route path='setting/delivery' element={<DeliverySettings />} />
            <Route path='setting/vacations' element={<VacationsSettings />} />
            <Route path='setting/assets' element={<Assets />} />
            <Route path='setting/branches' element={<BranchesSettings />} />
            <Route path='client-settings' element={<Settings />} />
            <Route path='client-transactions' element={<ClientTransactions />} />
            <Route path='doctor-settings' element={<DoctorSettings />} />
            <Route path='doctor-transactions' element={<DoctorTransactions />} />
            <Route path='profile' element={<Profile />} />
            <Route path='assets' element={<Navigate to="/setting/assets" replace />} />
            <Route path='activities' element={<Navigate to="/setting/activities" replace />} />
            <Route path='users' element={<Users />} />
            <Route path='users/:id' element={<UserDetails />} />
            <Route path='users/:id/edit' element={<UserEdit />} />
            <Route path='users/:id/versions' element={<UserVersions />} />
          </Route>

          {/* Public routes (no auth) */}
          <Route path='public/orders/:id' element={<PublicOrder />} />
          <Route path='public/invoices/:id' element={<PublicInvoice />} />

          <Route path='*' element={<NotFound />} />
  </Routes>
      </Suspense>
    </>
  )
}

export default AppRoutes
